import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SignalTradingChart } from './SignalTradingChart';
import { TrendingUp } from "lucide-react";

interface SignalChartsTabProps {
  initial_bots: any[];
}

export const SignalChartsTab: React.FC<SignalChartsTabProps> = ({ initial_bots }) => {
  const dynamicCoins = useMemo(() => {
    const defaultList = [
      'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT',
      'ADAUSDT', 'BNBUSDT', 'DOGEUSDT', 'LINKUSDT', 'LTCUSDT', 'NEARUSDT',
      'SUIUSDT', 'WIFUSDT', 'ONDOUSDT', 'AAVEUSDT', 'ENAUSDT', 'HBARUSDT', 'XLMUSDT', 'WLDUSDT', "ZECUSDT", "SAHARAUSDT", "SYRUPUSDT",
      'APTUSDT', 'AVAXUSDT', 'FETUSDT', 'TRXUSDT', "MANTRAUSDT", 'SNDKUSDT', 'AMDUSDT', 'MSFTUSDT'
    ];
    const botSymbols = (initial_bots || []).map((b: any) => b.symbol?.toUpperCase()).filter(Boolean);
    const set = new Set([...defaultList, ...botSymbols]);
    return Array.from(set).slice(0, 36); // 최상위 24개 코인
  }, [initial_bots]);

  const [selectedSymbol, setSelectedSymbol] = useState(dynamicCoins[0] || 'BTCUSDT');
  const [selectedStrategy, setSelectedStrategy] = useState('perfect_alignment_pullback');

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* 왼쪽 세로 코인 선택 리스트 */}
      <Card className="w-full lg:w-48 shrink-0 border-border/50 shadow-sm bg-card flex flex-col h-[600px] overflow-hidden">
        <CardHeader className="bg-muted/30 border-b border-border/50 p-4 shrink-0">
          <CardTitle className="text-xs font-black flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            코인 선택 목록
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 overflow-y-auto flex-1 space-y-1.5 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {dynamicCoins.map((sym) => {
            const isActive = selectedSymbol === sym;
            const hasActiveBot = (initial_bots || []).some((b: any) => b.symbol?.toUpperCase() === sym && b.is_active);
            
            return (
              <Button
                key={sym}
                variant={isActive ? "default" : "outline"}
                onClick={() => setSelectedSymbol(sym)}
                className={`w-full justify-start h-9 px-3 font-black text-xs rounded-xl transition-all relative ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "hover:bg-muted/50 border-border/50"
                }`}
              >
                <span className="truncate">{sym.replace('USDT', '')}</span>
                {hasActiveBot && (
                  <span className="absolute top-1 right-2 flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                  </span>
                )}
              </Button>
            );
          })}
        </CardContent>
      </Card>

      {/* 오른쪽 차트 및 전략 제어 영역 */}
      <div className="flex-1 w-full space-y-4">
        <Card className="border-border/50 shadow-sm bg-card p-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-muted/10">
          <div className="text-sm font-black text-foreground">
            차트 조회: <span className="text-primary font-black ml-1">{selectedSymbol}</span>
          </div>
          
          {/* 전략 필터 */}
          <div className="flex items-center gap-1.5 bg-muted/50 p-0.5 rounded-xl border border-border/50 text-[10px]">
            {[
              { val: 'perfect_alignment_pullback', label: 'Perfect Pullback' },
              { val: 'zerolag_macd', label: 'ZL-MACD' },
              { val: 'hybrid_pullback', label: 'Hybrid Pullback' }
            ].map((opt) => (
              <button
                key={opt.val}
                onClick={() => setSelectedStrategy(opt.val)}
                className={`rounded-lg font-bold text-[10px] px-2.5 py-1 transition-all ${
                  selectedStrategy === opt.val
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Card>

        {/* 대형 차트 */}
        <div className="relative border border-border/40 rounded-2xl overflow-hidden bg-card">
          <SignalTradingChart
            symbol={selectedSymbol}
            exchange="binance"
            interval="15m"
            strategyType={selectedStrategy}
          />
        </div>
      </div>
    </div>
  );
};
